import { createReducer, PayloadAction } from '@reduxjs/toolkit';
import { convertArrToObj } from 'lib/src/utils/generic';
import {
    fetchChallengesForGroupRequest,
    fetchChallengesForGroupSuccess,
    fetchChallengesForGroupFailure,
    fetchChallengesRequest,
    fetchChallengesSuccess,
    fetchChallengesFailure,
    deleteChallengeRequest,
    deleteChallengeFailure,
    deleteChallengeSuccess,
    createChallengeRequest,
    createChallengeSuccess,
    createChallengeFailure,
    editChallengeRequest,
    editChallengeSuccess,
    editChallengeFailure,
    fetchSingleChallengeRequest,
    fetchSingleChallengeSuccess,
    fetchSingleChallengeFailure,
    editChallengeLeadRequest,
    editChallengeLeadSuccess,
    editChallengeLeadFailure,
    editChallengeAssessorsRequest,
    editChallengeAssessorsSuccess,
    editChallengeAssessorsFailure,
    ChallengeAssessorsPayload,
    fetchChallengeAssessorsRequest,
    fetchChallengeAssessorsSuccess,
    fetchChallengeAssessorsFailure,
    fetchChallengeLeadRequest,
    fetchChallengeLeadSuccess,
    fetchChallengeLeadFailure,
    ChallengeLeadPayload,
    fetchChallengeScoreSummaryRequest,
    fetchChallengeScoreSummarySuccess,
    fetchChallengeScoreSummaryFailure,
    postMarkChallengeAsAssessmentCompleteRequest,
    postMarkChallengeAsAssessmentCompleteFailure,
    postMarkChallengeAsAssessmentCompleteSuccess,
    postMarkChallengeAsReadyForAssessmentRequest,
    postMarkChallengeAsReadyForAssessmentSuccess,
    postMarkChallengeAsReadyForAssessmentFailure,
    editApplicationSummaryRequest,
    editApplicationSummarySuccess,
    editApplicationSummaryFailure,
    fetchChallengeCalendarAdminOptionsRequest,
    fetchChallengeCalendarAdminOptionsSuccess,
    fetchChallengeCalendarAdminOptionsFailure,
    ChallengeCalendarAdminOptionsPayload,
    publishChallengeRequest,
    publishChallengeSuccess,
    publishChallengeFailure,
} from '@actions/challenges';
import {
    Challenge,
    ChallengeAssessor,
    ChallengeLead,
    ChallengeScoreSummary,
} from 'src/types/shared/Challenge';
import {
    editChallengeScoreWeightingFailure,
    editChallengeScoreWeightingRequest,
    editChallengeScoreWeightingSuccess,
} from '@actions/challenges/editChallengeScoreWeighting';
import { ChallengeCalendarAdminOption } from 'src/types/shared/User';

interface ChallengesState {
    isFetching: boolean;
    fetchError: string | null;
    isPosting: boolean;
    postSuccess: boolean;
    patchSuccess: boolean;
    postError: string | null;
    isPatching: boolean;
    patchError: string | null;
    isDeleting: boolean;
    deleteSuccess: boolean;
    deleteError: string | null;
    challenges: Record<number, Challenge>;
    assessors: Record<number, ChallengeAssessor>;
    leads: Record<number, ChallengeLead>;
    challengeScoreSummaries: Record<number, ChallengeScoreSummary>;
    calendarWorkshopAdmins: Record<number, ChallengeCalendarAdminOption>;
    calendarAssessors: Record<number, ChallengeCalendarAdminOption>;
    calendarPitchPresentationAdmins: Record<number, ChallengeCalendarAdminOption>;
}

const initialState: ChallengesState = {
    isFetching: false,
    fetchError: null,
    isPosting: false,
    postError: null,
    postSuccess: false,
    patchSuccess: false,
    isPatching: false,
    patchError: null,
    isDeleting: false,
    deleteError: null,
    deleteSuccess: false,
    challenges: {},
    assessors: {},
    leads: {},
    challengeScoreSummaries: {},
    calendarWorkshopAdmins: {},
    calendarAssessors: {},
    calendarPitchPresentationAdmins: {},
};

export default createReducer(initialState, {
    [fetchChallengesForGroupRequest.type]: handleFetchRequest,
    [fetchChallengesForGroupSuccess.type]: handleFetchChallengesForGroupSuccess,
    [fetchChallengesForGroupFailure.type]: handleFetchFailure,
    [fetchChallengesRequest.type]: handleFetchRequest,
    [fetchChallengesSuccess.type]: handleFetchChallengesSuccess,
    [fetchChallengesFailure.type]: handleFetchFailure,
    [deleteChallengeRequest.type]: handleDeleteRequest,
    [deleteChallengeFailure.type]: handleDeleteFailure,
    [deleteChallengeSuccess.type]: handleDeleteSuccess,
    [createChallengeRequest.type]: handlePostRequest,
    [createChallengeSuccess.type]: handlePostSuccess,
    [createChallengeFailure.type]: handlePostFailure,
    [editChallengeRequest.type]: handlePostRequest,
    [editChallengeSuccess.type]: handlePostSuccess,
    [editChallengeFailure.type]: handlePostFailure,
    [fetchSingleChallengeRequest.type]: handleFetchRequest,
    [fetchSingleChallengeSuccess.type]: handleFetchSingleChallengeSuccess,
    [fetchSingleChallengeFailure.type]: handleFetchFailure,
    [fetchChallengeAssessorsRequest.type]: handleFetchRequest,
    [fetchChallengeAssessorsSuccess.type]: handleFetchChallengeAssessorsSuccess,
    [fetchChallengeAssessorsFailure.type]: handleFetchFailure,
    [editChallengeAssessorsRequest.type]: handlePostRequest,
    [editChallengeAssessorsSuccess.type]: handleEditChallengeAssessorsSuccess,
    [editChallengeAssessorsFailure.type]: handlePostFailure,
    [fetchChallengeLeadRequest.type]: handleFetchRequest,
    [fetchChallengeLeadSuccess.type]: handleFetchChallengeLeadSuccess,
    [fetchChallengeLeadFailure.type]: handleFetchFailure,
    [editChallengeLeadRequest.type]: handlePatchRequest,
    [editChallengeLeadSuccess.type]: handleEditChallengeSuccess,
    [editChallengeLeadFailure.type]: handlePatchFailure,
    [editChallengeScoreWeightingRequest.type]: handlePatchRequest,
    [editChallengeScoreWeightingFailure.type]: handlePatchFailure,
    [editChallengeScoreWeightingSuccess.type]: handleEditChallengeSuccess,
    [fetchChallengeScoreSummaryRequest.type]: handleFetchRequest,
    [fetchChallengeScoreSummarySuccess.type]: handleFetchChallengeScoreSummarySuccess,
    [fetchChallengeScoreSummaryFailure.type]: handleFetchFailure,
    [postMarkChallengeAsAssessmentCompleteRequest.type]: handlePostRequest,
    [postMarkChallengeAsAssessmentCompleteSuccess.type]: handlePostSuccess,
    [postMarkChallengeAsAssessmentCompleteFailure.type]: handlePostFailure,
    [postMarkChallengeAsReadyForAssessmentRequest.type]: handlePostRequest,
    [postMarkChallengeAsReadyForAssessmentSuccess.type]: handlePostSuccess,
    [postMarkChallengeAsReadyForAssessmentFailure.type]: handlePostFailure,
    [editApplicationSummaryRequest.type]: handlePostRequest,
    [editApplicationSummarySuccess.type]: handleEditApplicationScoreSummarySuccess,
    [editApplicationSummaryFailure.type]: handlePostFailure,
    [fetchChallengeCalendarAdminOptionsRequest.type]: handleFetchRequest,
    [fetchChallengeCalendarAdminOptionsSuccess.type]: handleFetchChallengeCalendarAdminOptionsSuccess,
    [fetchChallengeCalendarAdminOptionsFailure.type]: handleFetchFailure,
    [publishChallengeRequest.type]: handlePatchRequest,
    [publishChallengeSuccess.type]: handlePublishSuccess,
    [publishChallengeFailure.type]: handlePatchFailure,
});

function handleFetchRequest(state: ChallengesState) {
    state.isFetching = true;
    state.fetchError = null;
}

function handleFetchFailure(state: ChallengesState, action: PayloadAction<string>) {
    state.fetchError = action.payload;
    state.isFetching = false;
}
function handlePostRequest(state: ChallengesState) {
    state.isPosting = true;
    state.postSuccess = false;
    state.postError = null;
}

function handlePostSuccess(state: ChallengesState, action: PayloadAction<Challenge>) {
    state.challenges[action.payload.id] = action.payload;
    state.isPosting = false;
    state.postSuccess = true;
}
function handlePostFailure(state: ChallengesState, action: PayloadAction<string>) {
    state.postError = action.payload;
    state.isPosting = false;
}
function handlePatchRequest(state: ChallengesState) {
    state.isPatching = true;
    state.patchError = null;
    state.patchSuccess = false;
}

function handlePatchFailure(state: ChallengesState, action: PayloadAction<string>) {
    state.patchError = action.payload;
    state.isPatching = false;
    state.patchSuccess = false;
}
function handleDeleteRequest(state: ChallengesState) {
    state.isDeleting = true;
    state.deleteSuccess = false;
    state.deleteError = null;
}

function handleDeleteSuccess(state: ChallengesState, action: PayloadAction<number>) {
    delete state.challenges[action.payload];
    state.isDeleting = false;
    state.deleteSuccess = true;
}
function handleDeleteFailure(state: ChallengesState, action: PayloadAction<string>) {
    state.deleteError = action.payload;
    state.isDeleting = false;
}

function handleFetchChallengesForGroupSuccess(
    state: ChallengesState,
    action: PayloadAction<Challenge[]>,
) {
    state.challenges = convertArrToObj(action.payload);
    state.isFetching = false;
}

function handleFetchChallengesSuccess(state: ChallengesState, action: PayloadAction<Challenge[]>) {
    state.challenges = convertArrToObj(action.payload);
    state.isFetching = false;
}

function handleFetchSingleChallengeSuccess(
    state: ChallengesState,
    action: PayloadAction<Challenge>,
) {
    state.challenges[action.payload.id] = action.payload;
    state.isFetching = false;
}

function handleFetchChallengeAssessorsSuccess(
    state: ChallengesState,
    action: PayloadAction<ChallengeAssessorsPayload>,
) {
    state.isFetching = false;
    state.assessors = convertArrToObj(action.payload.users);
}

function handleEditChallengeAssessorsSuccess(state: ChallengesState) {
    state.isPosting = false;
    state.postSuccess = true;
}

function handleFetchChallengeLeadSuccess(
    state: ChallengesState,
    action: PayloadAction<ChallengeLeadPayload>,
) {
    state.isFetching = false;
    state.leads = convertArrToObj(action.payload.users);
}

function handleEditChallengeSuccess(state: ChallengesState, action: PayloadAction<Challenge>) {
    state.challenges[action.payload.id] = action.payload;
    state.isPatching = false;
    state.patchSuccess = true;
}

function handleFetchChallengeScoreSummarySuccess(
    state: ChallengesState,
    action: PayloadAction<ChallengeScoreSummary>,
) {
    state.isFetching = false;
    state.challengeScoreSummaries[action.payload.challengeID] = action.payload;
}
function handleEditApplicationScoreSummarySuccess(state: ChallengesState) {
    state.isPosting = false;
    state.postSuccess = true;
}

function handleFetchChallengeCalendarAdminOptionsSuccess(
    state: ChallengesState,
    action: PayloadAction<ChallengeCalendarAdminOptionsPayload>,
) {
    state.calendarWorkshopAdmins = convertArrToObj(action.payload.workshopAdmins);
    state.calendarAssessors = convertArrToObj(action.payload.assessors);
    state.calendarPitchPresentationAdmins = convertArrToObj(action.payload.pitchPresentationAdmins);
    state.isFetching = false;
}

function handlePublishSuccess(state: ChallengesState, action: PayloadAction<Challenge>) {
    state.isPatching = false;
    state.patchSuccess = true;
    state.challenges[action.payload.id] = action.payload;
}
